import React, { useEffect, useRef } from 'react';
import { getOrCreateAnonymousId } from './utils/anonymousId';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { trackPageView } from './utils/metaPixel';
import { flushLeadQueue } from './utils/leadQueue';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import HomePage from './pages/HomePage';
import PaymentPage from './pages/PaymentPage';
import SuccessPage from './pages/SuccessPage';
import ErrorPage from './pages/ErrorPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionPaymentPage from './pages/SubscriptionPaymentPage';
import SubscriptionConfirmPage from './pages/SubscriptionConfirmPage';
import WelcomePage from './pages/WelcomePage';
import FormPage from './pages/FormPage';
import QualifiedPostFormPage from './pages/QualifiedPostFormPage';
import NewsletterPage from './pages/NewsletterPage';
import PrivacyPage from './pages/PrivacyPage';
import ReviewFormPage from './pages/ReviewFormPage';
import AdminLeadsPage from './pages/AdminLeadsPage';
import AdminFinanzasPage from './pages/AdminFinanzasPage';
import AdminEmailPage from './pages/AdminEmailPage';
import AdminSettingPage from './pages/AdminSettingPage';
import NutritionOnePage from './pages/NutritionOnePage';
import CalorieMacroCalculatorPage from './pages/CalorieMacroCalculatorPage';
import FiveDaySplitPage from './pages/FiveDaySplitPage';
import AntiAtraconesPage from './pages/AntiAtraconesPage';
import PeriodizacionPage from './pages/PeriodizacionPage';
import ProgresionPage from './pages/ProgresionPage';
import EquivalenciasPage from './pages/EquivalenciasPage';
import FourDaySplitPage from './pages/FourDaySplitPage';
import SleepGuidePage from './pages/SleepGuidePage';
import BloodWorkGuidePage from './pages/BloodWorkGuidePage';
import ScrollToTop from './components/ScrollToTop';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      'Kanit',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: 'Kanit, sans-serif',
      fontWeight: 500,
    },
  },
});

/**
 * PageView en cada navegación interna. El snippet de index.html ya dispara el de
 * la carga inicial, así que el primero se salta para no contarlo dos veces.
 * Sin esto, /form y /qualified nunca generaban PageView y las audiencias de
 * retargeting por URL visitada quedaban vacías.
 */
function PixelRouteTracker() {
  const location = useLocation();
  const primeraCarga = useRef(true);

  useEffect(() => {
    if (primeraCarga.current) {
      primeraCarga.current = false;
      return;
    }
    trackPageView();
  }, [location.pathname]);

  return null;
}

/**
 * Red de seguridad para URLs que no existen. Sin esto, cualquier dirección mal
 * escrita (un anuncio con la URL mal armada, un link mal copiado en un mensaje)
 * carga la app y deja la pantalla en blanco: React Router no encuentra ninguna
 * ruta y no renderiza nada. El servidor responde 200, así que no se nota.
 * Con tráfico pagado eso es plata quemada, así que se manda a la home.
 * La query string se preserva para no perder las UTMs de la campaña.
 */
function RedirigirAHome() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
}

function App() {
  useEffect(() => {
    getOrCreateAnonymousId();
    // Recupera leads que quedaron sin enviar (backend caído, red cortada).
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    flushLeadQueue(backendUrl);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <PixelRouteTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/form" element={<FormPage />} />
          {/* OBSOLETA (2026-07-30): monto viejo de $120.000, el cobro real va por /subscribe */}
          <Route path="/unique-payment" element={<PaymentPage />} />
          <Route path="/subscribe" element={<SubscriptionPage />} />
          <Route path="/subscribe/pago" element={<SubscriptionPaymentPage />} />
          <Route path="/subscribe/confirmar" element={<SubscriptionConfirmPage />} />
          <Route path="/qualified" element={<QualifiedPostFormPage />} />
          <Route path="/success-request" element={<SuccessPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
          <Route path="/clients/review" element={<ReviewFormPage />} />
          <Route path="/nutrition-1" element={<NutritionOnePage />} />
          <Route path="/calorie-macro-calculator" element={<CalorieMacroCalculatorPage />} />
          <Route path="/5-day-split" element={<FiveDaySplitPage />} />
          <Route path="/4-day-split" element={<FourDaySplitPage />} />
          <Route path="/sleep-guide" element={<SleepGuidePage />} />
          <Route path="/anti-atracones" element={<AntiAtraconesPage />} />
          <Route path="/periodizacion" element={<PeriodizacionPage />} />
          <Route path="/progresion" element={<ProgresionPage />} />
          <Route path="/equivalencias" element={<EquivalenciasPage />} />
          <Route path="/examenes" element={<BloodWorkGuidePage />} />
          <Route path="/blood-work" element={<Navigate to="/examenes" replace />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
          <Route path="/admin/finanzas" element={<AdminFinanzasPage />} />
          <Route path="/admin/email" element={<AdminEmailPage />} />
          <Route path="/admin/setting" element={<AdminSettingPage />} />
          {/* Cualquier ruta desconocida cae a la home en vez de pantalla en blanco */}
          <Route path="*" element={<RedirigirAHome />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 